import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleReset = async () => {
    if (!resetEmail) {
      toast({
        title: 'Error',
        description: 'Please enter your email address',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      // In a real app, this would send an email. For now, we'll show instructions.
      toast({
        title: 'Password Reset Instructions',
        description: `Please contact your administrator at admin@rsooutfits.com to reset your password for ${resetEmail}`,
      });
      setShowReset(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to process reset request',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password);
      } else {
        result = await register(email, password, name, 'editor');
      }

      if (result.success) {
        navigate('/dashboard');
      } else {
        toast({
          title: 'Error',
          description: result.error,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1551446429-4ca74634fc94?crop=entropy&cs=srgb&fm=jpg&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-primary/50"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-primary-foreground text-center">
            <h1 className="text-5xl font-bold mb-4 font-heading tracking-tight">RSO OUTFITS</h1>
            <p className="text-xl tracking-wide uppercase text-primary-foreground/80">Inventory Command System</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div>
            <h2 className="text-3xl font-bold tracking-tight font-heading" data-testid="auth-title">
              {showReset ? 'Reset Password' : (isLogin ? 'Sign In' : 'Create Account')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {showReset 
                ? 'Enter your email to reset your password'
                : (isLogin ? 'Access your inventory dashboard' : 'Register for RSO Inventory System')
              }
            </p>
          </div>

          {showReset ? (
            <div className="space-y-6">
              <div>
                <label htmlFor="reset-email" className="block text-sm font-medium mb-2">
                  Email Address
                </label>
                <Input
                  id="reset-email"
                  data-testid="reset-email-input"
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <Button
                onClick={handleReset}
                data-testid="reset-button"
                className="w-full"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Request Password Reset'}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowReset(false);
                    setResetEmail('');
                  }}
                  data-testid="back-to-login"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-6">
                {!isLogin && (
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Full Name
                    </label>
                    <Input
                      id="name"
                      data-testid="name-input"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required={!isLogin}
                      placeholder="John Doe"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium mb-2">
                    Password
                  </label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                  />
                </div>

                {isLogin && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowReset(true)}
                      data-testid="forgot-password-link"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="submit-button"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
                </Button>
              </form>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setIsLogin(!isLogin)}
                  data-testid="toggle-auth-mode"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}